import React, { useState, useEffect, useCallback } from 'react';
import { FINANCIAL_API, authFetch } from '../constants';
import TabBar from '../components/ui/TabBar';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';
import { useGlobalEntity } from '../hooks/useGlobalEntity';

const CONSOLIDATED = '__CONSOLIDATED__';

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtSigned = (n: number) =>
  n < 0 ? `(${fmt(Math.abs(n))})` : fmt(n);

const fmtImpact = (impact: number) =>
  Math.abs(impact) < 0.005 ? '\u2014' : (impact > 0 ? `+${fmt(impact)}` : `(${fmt(Math.abs(impact))})`);

type ViewMode = 'before' | 'conversion' | 'reporting' | 'adjustments' | 'after' | 'after_all' | 'comparison';
type CFItem = { account_code: string; account_name: string; amount: number };

export default function CashFlowStatement() {
  const { entity: globalEntity } = useGlobalEntity();
  const [entities, setEntities] = useState<any[]>([]);
  const [entity, setEntity] = useState('');
  const [periods, setPeriods] = useState<string[]>([]);
  const [period, setPeriod] = useState('');
  const [view, setView] = useState<ViewMode>('after');
  const [data, setData] = useState<any>(null);
  const [beforeData, setBeforeData] = useState<any>(null);
  const [afterData, setAfterData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showUSD, setShowUSD] = useState(false);
  const [exporting, setExporting] = useState<string | null>(null);
  const [pendingInfo, setPendingInfo] = useState<any>(null);

  const entityCurrency = entities.find(e => e.entity_code === entity)?.functional_currency || '';
  const isUSD = entity === CONSOLIDATED || entityCurrency === 'USD';
  const isConsolidated = entity === CONSOLIDATED;
  const isComparison = view === 'comparison';

  useEffect(() => {
    authFetch(`${FINANCIAL_API}/entities`)
      .then(r => r.json())
      .then(rows => {
        setEntities(rows);
        if (rows.length > 0 && !entity) setEntity(globalEntity || rows[0].entity_code);
      });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (globalEntity && globalEntity !== entity) setEntity(globalEntity);
  }, [globalEntity]); // eslint-disable-line

  useEffect(() => {
    if (!entity) return;
    const url = entity === CONSOLIDATED
      ? `${FINANCIAL_API}/consolidated/available-periods`
      : `${FINANCIAL_API}/available-periods?entity_code=${entity}`;
    authFetch(url)
      .then(r => r.json())
      .then(d => {
        setPeriods(d.periods || []);
        if (d.periods?.length) setPeriod(d.periods[d.periods.length - 1]);
      });
    setShowUSD(false);
  }, [entity]);

  const buildUrl = useCallback((v: string) => {
    const currSuffix = showUSD && !isUSD ? '&currency=USD' : '';
    return isConsolidated
      ? `${FINANCIAL_API}/consolidated/cash-flow?period=${period}&view=${v}${currSuffix}`
      : `${FINANCIAL_API}/cash-flow?entity_code=${entity}&period=${period}&view=${v}${currSuffix}`;
  }, [entity, period, showUSD, isUSD, isConsolidated]);

  const loadData = useCallback(() => {
    if (!entity || !period) return;
    setLoading(true);

    if (isComparison) {
      Promise.all([
        authFetch(buildUrl('before')).then(r => r.json()),
        authFetch(buildUrl('after')).then(r => r.json()),
      ]).then(([b, a]) => {
        setBeforeData(b);
        setAfterData(a);
        setData(a);
        setLoading(false);
      }).catch(() => setLoading(false));
    } else {
      authFetch(buildUrl(view))
        .then(r => r.json())
        .then(d => { setData(d); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [entity, period, view, showUSD, isUSD, isConsolidated, isComparison, buildUrl]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!entity) return;
    const url = isConsolidated
      ? `${FINANCIAL_API}/consolidated/pending-adjustments`
      : `${FINANCIAL_API}/pending-adjustments?entity_code=${entity}`;
    authFetch(url).then(r => r.json()).then(setPendingInfo).catch(() => setPendingInfo(null));
  }, [entity, isConsolidated]);

  const handleExport = async (format: 'pdf' | 'excel') => {
    setExporting(format);
    try {
      const body = {
        statement: 'cash-flow',
        entity_code: isConsolidated ? 'CONSOLIDATED' : entity,
        period, view,
        currency: showUSD && !isUSD ? 'USD' : undefined,
      };
      const resp = await authFetch(`${FINANCIAL_API}/export/${format}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cash-flow-${entity}-${period}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export failed:', err); }
    setExporting(null);
  };

  const needsConversion = (() => {
    if (isConsolidated) return true;
    const ent = entities.find(e => e.entity_code === entity);
    return ent && ent.local_framework && ent.local_framework !== ent.reporting_framework;
  })();

  const allViewButtons: { key: ViewMode; label: string }[] = [
    { key: 'before', label: 'Local' },
    { key: 'conversion', label: 'Conversion' },
    { key: 'reporting', label: 'Reporting' },
    { key: 'adjustments', label: 'Adjustments' },
    { key: 'after', label: 'After' },
    { key: 'after_all', label: 'After (All)' },
    { key: 'comparison', label: 'Comparison' },
  ];
  const viewButtons = allViewButtons.filter(v =>
    needsConversion || !['conversion', 'reporting'].includes(v.key)
  );

  const cfImpactColor = (impact: number) => {
    if (Math.abs(impact) < 0.005) return 'var(--text-hint)';
    return impact > 0 ? 'var(--accent-good)' : 'var(--accent-critical)';
  };

  // Normal view renderers
  const renderItems = (items: CFItem[]) => (
    <>
      {items.map((a, idx) => (
        <tr key={a.account_code || idx} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <td style={tdIndent} className="mono">{a.account_code}</td>
          <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>{a.account_name}</td>
          <td style={{
            ...tdRight,
            color: a.amount < 0 ? 'var(--accent-critical)' : 'var(--text-primary)',
          }}>{fmtSigned(a.amount)}</td>
        </tr>
      ))}
    </>
  );

  const renderSubtotal = (label: string, amount: number, bold = false) => (
    <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: bold ? 'var(--accent-subtle)' : 'transparent' }}>
      <td style={tdStyle}></td>
      <td style={{ ...tdStyle, fontWeight: bold ? 700 : 600, color: 'var(--text-primary)' }}>{label}</td>
      <td style={{
        ...tdRight,
        fontWeight: bold ? 700 : 600,
        color: amount < 0 ? 'var(--accent-critical)' : amount > 0 ? 'var(--accent-good)' : 'var(--text-primary)',
        borderTop: bold ? '2px solid var(--border-subtle)' : '1px solid var(--text-hint)',
      }}>{fmtSigned(amount)}</td>
    </tr>
  );

  const renderSectionHeader = (label: string, colSpan = 3) => (
    <tr style={{ background: 'rgba(0,0,0,0.02)' }}>
      <td colSpan={colSpan} style={{
        padding: '10px 14px 4px',
        fontSize: 11, fontWeight: 600,
        color: 'var(--text-hint)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>{label}</td>
    </tr>
  );

  // Comparison view renderers
  const renderCompItems = (bItems: CFItem[], aItems: CFItem[]) => {
    const allCodes = new Set([
      ...bItems.map(i => i.account_code),
      ...aItems.map(i => i.account_code),
    ]);
    return Array.from(allCodes).map(code => {
      const bAmt = bItems.find(i => i.account_code === code)?.amount || 0;
      const aAmt = aItems.find(i => i.account_code === code)?.amount || 0;
      const impact = aAmt - bAmt;
      const name = (aItems.find(i => i.account_code === code) || bItems.find(i => i.account_code === code))?.account_name || '';
      return (
        <tr key={code} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          <td style={tdIndent} className="mono">{code}</td>
          <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>{name}</td>
          <td style={tdRight}>{fmtSigned(bAmt)}</td>
          <td style={tdRight}>{fmtSigned(aAmt)}</td>
          <td style={{ ...tdRight, fontWeight: 600, color: cfImpactColor(impact) }}>
            {fmtImpact(impact)}
          </td>
        </tr>
      );
    });
  };

  const renderCompSubtotal = (label: string, bAmt: number, aAmt: number, bold = false) => {
    const impact = aAmt - bAmt;
    return (
      <tr style={{ borderBottom: '1px solid var(--border-subtle)', background: bold ? 'var(--accent-subtle)' : 'transparent' }}>
        <td style={tdStyle}></td>
        <td style={{ ...tdStyle, fontWeight: bold ? 700 : 600, color: 'var(--text-primary)' }}>{label}</td>
        <td style={{ ...tdRight, fontWeight: bold ? 700 : 600, borderTop: bold ? '2px solid var(--border-subtle)' : '1px solid var(--text-hint)' }}>
          {fmtSigned(bAmt)}
        </td>
        <td style={{ ...tdRight, fontWeight: bold ? 700 : 600, borderTop: bold ? '2px solid var(--border-subtle)' : '1px solid var(--text-hint)' }}>
          {fmtSigned(aAmt)}
        </td>
        <td style={{ ...tdRight, fontWeight: bold ? 700 : 600, color: cfImpactColor(impact), borderTop: bold ? '2px solid var(--border-subtle)' : '1px solid var(--text-hint)' }}>
          {fmtImpact(impact)}
        </td>
      </tr>
    );
  };

  const renderCompValueRow = (label: string, bAmt: number, aAmt: number, isBold = false) => {
    const impact = aAmt - bAmt;
    return (
      <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
        <td style={tdIndent}></td>
        <td style={{ ...tdStyle, fontWeight: isBold ? 600 : 400, color: 'var(--text-primary)' }}>{label}</td>
        <td style={{ ...tdRight, fontWeight: isBold ? 600 : 400 }}>{fmtSigned(bAmt)}</td>
        <td style={{ ...tdRight, fontWeight: isBold ? 600 : 400 }}>{fmtSigned(aAmt)}</td>
        <td style={{ ...tdRight, fontWeight: 600, color: cfImpactColor(impact) }}>{fmtImpact(impact)}</td>
      </tr>
    );
  };

  const bs = beforeData?.sections;
  const as_ = afterData?.sections;
  const s = data?.sections;

  return (
    <div style={{ maxWidth: isComparison ? 1100 : 900, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy-900)', margin: 0 }}>
          Cash Flow Statement
        </h2>
        {data && (
          <p style={{ fontSize: 13, color: 'var(--text-hint)', margin: '4px 0 0' }}>
            {data.entity_name} &middot; Period: {data.period} &middot; Currency: {data.currency}
            {entity === CONSOLIDATED && ' (Consolidated)'}
            {isComparison && ' \u00b7 Comparison: Before vs After'}
          </p>
        )}
      </div>

      <FilterBar filters={[
        {
          key: 'entity', label: 'Entity', type: 'select', value: entity,
          onChange: v => setEntity(v),
          options: [
            ...entities.map(e => ({ value: e.entity_code, label: `${e.entity_name} (${e.functional_currency})` })),
            { value: CONSOLIDATED, label: 'Consolidated (USD)' },
          ],
          placeholder: 'Select Entity',
        },
        {
          key: 'period', label: 'Period', type: 'select', value: period,
          onChange: v => setPeriod(v),
          options: periods.map(p => ({ value: p, label: p })),
          placeholder: 'Select Period',
        },
      ]} />

      <TabBar
        tabs={viewButtons.map(v => ({ key: v.key, label: v.label }))}
        active={view}
        onChange={k => setView(k as ViewMode)}
      />

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        {!isUSD && (
          <ActionButton
            label={showUSD ? 'USD' : entityCurrency}
            onClick={() => setShowUSD(!showUSD)}
            variant={showUSD ? 'primary' : 'secondary'}
            size="sm"
          />
        )}
        <ActionButton label={exporting === 'pdf' ? '...' : 'PDF'} onClick={() => handleExport('pdf')} variant="ghost" size="sm" disabled={!!exporting || !data} />
        <ActionButton label={exporting === 'excel' ? '...' : 'Excel'} onClick={() => handleExport('excel')} variant="ghost" size="sm" disabled={!!exporting || !data} />
        {loading && <span style={{ fontSize: 12, color: 'var(--text-hint)' }}>Loading...</span>}
      </div>

      {/* Provisional warning banner */}
      {view === 'after_all' && (
        <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-border)', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>!</span>
          <span style={{ fontSize: 12, color: 'var(--warning)' }}>This view includes <strong>PROVISIONAL</strong> adjustments not yet confirmed by a supervisor.</span>
        </div>
      )}

      {/* Pending adjustments card */}
      {pendingInfo && (pendingInfo.provisional_jes?.count > 0 || pendingInfo.pending_actions?.count > 0 || pendingInfo.unresolved_exceptions?.total > 0) && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', gap: 24, alignItems: 'center', fontSize: 12 }}>
          <span style={{ color: 'var(--text-hint)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pending</span>
          {pendingInfo.provisional_jes?.count > 0 && (
            <span style={{ color: 'var(--warning)' }}>{pendingInfo.provisional_jes.count} provisional JE{pendingInfo.provisional_jes.count !== 1 ? 's' : ''}</span>
          )}
          {pendingInfo.pending_actions?.count > 0 && (
            <span style={{ color: 'var(--navy-700)' }}>{pendingInfo.pending_actions.count} action{pendingInfo.pending_actions.count !== 1 ? 's' : ''} in review</span>
          )}
          {pendingInfo.unresolved_exceptions?.total > 0 && (
            <span style={{ color: 'var(--text-hint)' }}>{pendingInfo.unresolved_exceptions.total} unresolved exception{pendingInfo.unresolved_exceptions.total !== 1 ? 's' : ''}</span>
          )}
        </div>
      )}

      {/* === COMPARISON VIEW === */}
      {isComparison && bs && as_ && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 100 }}>Code</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 130 }}>Before</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 130 }}>After</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 130 }}>Impact</th>
              </tr>
            </thead>
            <tbody>
              {/* OPERATING */}
              {renderSectionHeader('Cash Flows from Operating Activities', 5)}

              {renderCompValueRow('Net Income', bs.operating?.net_income || 0, as_.operating?.net_income || 0, true)}

              {((bs.operating?.adjustments?.items?.length || 0) > 0 || (as_.operating?.adjustments?.items?.length || 0) > 0) && (
                <>
                  {renderSectionHeader(bs.operating?.adjustments?.label || 'Non-Cash Adjustments', 5)}
                  {renderCompItems(
                    bs.operating?.adjustments?.items || [],
                    as_.operating?.adjustments?.items || [],
                  )}
                  {renderCompSubtotal('Total Adjustments',
                    bs.operating?.adjustments?.subtotal || 0,
                    as_.operating?.adjustments?.subtotal || 0)}
                </>
              )}

              {((bs.operating?.working_capital?.items?.length || 0) > 0 || (as_.operating?.working_capital?.items?.length || 0) > 0) && (
                <>
                  {renderSectionHeader(bs.operating?.working_capital?.label || 'Working Capital Changes', 5)}
                  {renderCompItems(
                    bs.operating?.working_capital?.items || [],
                    as_.operating?.working_capital?.items || [],
                  )}
                  {renderCompSubtotal('Total Working Capital Changes',
                    bs.operating?.working_capital?.subtotal || 0,
                    as_.operating?.working_capital?.subtotal || 0)}
                </>
              )}

              {renderCompSubtotal('Net Cash from Operating Activities',
                bs.operating?.subtotal || 0,
                as_.operating?.subtotal || 0, true)}

              <tr><td colSpan={5} style={{ height: 12 }}></td></tr>

              {/* INVESTING */}
              {renderSectionHeader('Cash Flows from Investing Activities', 5)}
              {((bs.investing?.items?.length || 0) > 0 || (as_.investing?.items?.length || 0) > 0) ? (
                renderCompItems(bs.investing?.items || [], as_.investing?.items || [])
              ) : (
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={tdIndent}></td>
                  <td style={{ ...tdStyle, color: 'var(--text-hint)', fontStyle: 'italic' }}>None</td>
                  <td style={tdRight}>0.00</td>
                  <td style={tdRight}>0.00</td>
                  <td style={{ ...tdRight, color: 'var(--text-hint)' }}>{'\u2014'}</td>
                </tr>
              )}
              {renderCompSubtotal('Net Cash from Investing Activities',
                bs.investing?.subtotal || 0,
                as_.investing?.subtotal || 0, true)}

              <tr><td colSpan={5} style={{ height: 12 }}></td></tr>

              {/* FINANCING */}
              {renderSectionHeader('Cash Flows from Financing Activities', 5)}
              {((bs.financing?.items?.length || 0) > 0 || (as_.financing?.items?.length || 0) > 0) ? (
                renderCompItems(bs.financing?.items || [], as_.financing?.items || [])
              ) : (
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={tdIndent}></td>
                  <td style={{ ...tdStyle, color: 'var(--text-hint)', fontStyle: 'italic' }}>None</td>
                  <td style={tdRight}>0.00</td>
                  <td style={tdRight}>0.00</td>
                  <td style={{ ...tdRight, color: 'var(--text-hint)' }}>{'\u2014'}</td>
                </tr>
              )}
              {renderCompSubtotal('Net Cash from Financing Activities',
                bs.financing?.subtotal || 0,
                as_.financing?.subtotal || 0, true)}

              {/* FX Effect (consolidated) */}
              {(beforeData.fx_effect !== undefined || afterData.fx_effect !== undefined) &&
               ((beforeData.fx_effect || 0) !== 0 || (afterData.fx_effect || 0) !== 0) && (
                <>
                  <tr><td colSpan={5} style={{ height: 12 }}></td></tr>
                  {renderSectionHeader('Effect of Exchange Rates on Cash', 5)}
                  {renderCompValueRow('FX Translation Effect',
                    beforeData.fx_effect || 0,
                    afterData.fx_effect || 0)}
                </>
              )}

              <tr><td colSpan={5} style={{ height: 12 }}></td></tr>

              {/* NET CHANGE */}
              {(() => {
                const bNC = bs.net_change_in_cash || 0;
                const aNC = as_.net_change_in_cash || 0;
                const ncImpact = aNC - bNC;
                return (
                  <tr style={{ background: 'rgba(0,0,0,0.03)', borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={tdStyle}></td>
                    <td style={{ ...tdStyle, fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                      Net Change in Cash
                    </td>
                    <td style={{ ...tdRight, fontWeight: 700, fontSize: 14, borderTop: '2px solid var(--border-subtle)' }}>
                      {fmtSigned(bNC)}
                    </td>
                    <td style={{ ...tdRight, fontWeight: 700, fontSize: 14, borderTop: '2px solid var(--border-subtle)' }}>
                      {fmtSigned(aNC)}
                    </td>
                    <td style={{
                      ...tdRight, fontWeight: 700, fontSize: 14,
                      borderTop: '2px solid var(--border-subtle)',
                      color: cfImpactColor(ncImpact),
                    }}>{fmtImpact(ncImpact)}</td>
                  </tr>
                );
              })()}

              <tr><td colSpan={5} style={{ height: 8 }}></td></tr>

              {/* Cash reconciliation */}
              {renderCompValueRow('Beginning Cash', beforeData.beginning_cash || 0, afterData.beginning_cash || 0)}
              {renderCompValueRow('Ending Cash', beforeData.ending_cash || 0, afterData.ending_cash || 0, true)}
            </tbody>
          </table>
        </div>
      )}

      {/* === NORMAL VIEW === */}
      {!isComparison && data && s && (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 100 }}>Code</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Description</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 160 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {/* OPERATING ACTIVITIES */}
              {renderSectionHeader('Cash Flows from Operating Activities')}

              <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={tdIndent}></td>
                <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary)' }}>Net Income</td>
                <td style={{ ...tdRight, fontWeight: 600 }}>{fmtSigned(s.operating.net_income)}</td>
              </tr>

              {s.operating.adjustments.items.length > 0 && (
                <>
                  {renderSectionHeader(s.operating.adjustments.label)}
                  {renderItems(s.operating.adjustments.items)}
                  {renderSubtotal('Total Adjustments', s.operating.adjustments.subtotal)}
                </>
              )}

              {s.operating.working_capital.items.length > 0 && (
                <>
                  {renderSectionHeader(s.operating.working_capital.label)}
                  {renderItems(s.operating.working_capital.items)}
                  {renderSubtotal('Total Working Capital Changes', s.operating.working_capital.subtotal)}
                </>
              )}

              {renderSubtotal('Net Cash from Operating Activities', s.operating.subtotal, true)}

              <tr><td colSpan={3} style={{ height: 12 }}></td></tr>

              {/* INVESTING ACTIVITIES */}
              {renderSectionHeader('Cash Flows from Investing Activities')}
              {s.investing.items.length > 0 ? renderItems(s.investing.items) : (
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={tdIndent}></td>
                  <td style={{ ...tdStyle, color: 'var(--text-hint)', fontStyle: 'italic' }}>None</td>
                  <td style={tdRight}>0.00</td>
                </tr>
              )}
              {renderSubtotal('Net Cash from Investing Activities', s.investing.subtotal, true)}

              <tr><td colSpan={3} style={{ height: 12 }}></td></tr>

              {/* FINANCING ACTIVITIES */}
              {renderSectionHeader('Cash Flows from Financing Activities')}
              {s.financing.items.length > 0 ? renderItems(s.financing.items) : (
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={tdIndent}></td>
                  <td style={{ ...tdStyle, color: 'var(--text-hint)', fontStyle: 'italic' }}>None</td>
                  <td style={tdRight}>0.00</td>
                </tr>
              )}
              {renderSubtotal('Net Cash from Financing Activities', s.financing.subtotal, true)}

              {/* FX Effect (consolidated only) */}
              {data.fx_effect !== undefined && data.fx_effect !== 0 && (
                <>
                  <tr><td colSpan={3} style={{ height: 12 }}></td></tr>
                  {renderSectionHeader('Effect of Exchange Rates on Cash')}
                  <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={tdIndent}></td>
                    <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>FX Translation Effect</td>
                    <td style={tdRight}>{fmtSigned(data.fx_effect)}</td>
                  </tr>
                </>
              )}

              <tr><td colSpan={3} style={{ height: 12 }}></td></tr>

              {/* NET CHANGE */}
              <tr style={{ background: 'var(--accent-subtle)', borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={tdStyle}></td>
                <td style={{ ...tdStyle, fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                  Net Change in Cash
                </td>
                <td style={{
                  ...tdRight, fontWeight: 700, fontSize: 14,
                  borderTop: '2px solid var(--border-subtle)',
                  color: s.net_change_in_cash < 0 ? 'var(--accent-critical)' : 'var(--accent-good)',
                }}>{fmtSigned(s.net_change_in_cash)}</td>
              </tr>

              <tr><td colSpan={3} style={{ height: 8 }}></td></tr>

              {/* Cash reconciliation */}
              <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={tdIndent}></td>
                <td style={{ ...tdStyle, color: 'var(--text-primary)' }}>Beginning Cash</td>
                <td style={tdRight}>{fmtSigned(data.beginning_cash)}</td>
              </tr>
              <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={tdIndent}></td>
                <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text-primary)' }}>Ending Cash</td>
                <td style={{ ...tdRight, fontWeight: 600 }}>{fmtSigned(data.ending_cash)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Balance check (normal view) */}
      {!isComparison && data && (
        <div style={{
          marginTop: 16, padding: '10px 16px', borderRadius: 8,
          background: data.is_balanced ? 'var(--positive-bg)' : 'var(--negative-bg)',
          border: `1px solid ${data.is_balanced ? 'var(--accent-good)' : 'var(--accent-critical)'}`,
          fontSize: 13, fontWeight: 600,
          color: data.is_balanced ? 'var(--accent-good)' : 'var(--accent-critical)',
        }}>
          {data.is_balanced
            ? `Cash Flow Balanced: Beginning (${fmtSigned(data.beginning_cash)}) + Net Change (${fmtSigned(data.sections?.net_change_in_cash || 0)}) = Ending (${fmtSigned(data.ending_cash)})`
            : `Cash Flow NOT Balanced! Computed Ending: ${fmtSigned(data.balance_check?.computed_ending || 0)}, Actual Ending: ${fmtSigned(data.balance_check?.actual_ending || 0)}, Diff: ${fmtSigned(data.balance_check?.difference || 0)}`
          }
        </div>
      )}

      {/* Comparison summary */}
      {isComparison && bs && as_ && (
        <div style={{
          marginTop: 16, padding: '10px 16px', borderRadius: 8,
          background: 'var(--accent-subtle)',
          border: '1px solid var(--accent-border)',
          fontSize: 13, fontWeight: 600, color: 'var(--accent)',
        }}>
          Net Cash Impact: {fmtImpact((as_.net_change_in_cash || 0) - (bs.net_change_in_cash || 0))} {data?.currency}
          {' \u00b7 '}
          Ending Cash: {fmtSigned(beforeData?.ending_cash || 0)} {'\u2192'} {fmtSigned(afterData?.ending_cash || 0)}
        </div>
      )}

      {data && !isComparison && !data.sections && !loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 14 }}>
          No data for this entity/period/view combination.
        </div>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px', textAlign: 'left',
  fontSize: 11, fontWeight: 600, color: 'var(--text-hint)',
  textTransform: 'uppercase', letterSpacing: '0.04em',
  borderBottom: '2px solid var(--border-subtle)',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)',
};

const tdIndent: React.CSSProperties = {
  ...tdStyle, paddingLeft: 28, fontVariantNumeric: 'tabular-nums', fontSize: 12, color: 'var(--text-hint)',
};

const tdRight: React.CSSProperties = {
  ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums',
};
